import React, { useCallback, useEffect, useMemo, useRef } from "react"
import Calendar from "react-calendar"
import estilosCalendario from "!!raw-loader!./Calendario.css";
import css from "!!raw-loader!../../../../node_modules/react-calendar/dist/Calendar.css";
import "./dialogCalendario.scss";

/**
 * Classe do `<Dialog>` que existe só para carregar o calendário — apaga o Paper do MUI (fundo,
 * sombra e canto), senão os cantos quadrados dele sobram por fora do cartão arredondado do
 * calendário. Regra em `dialogCalendario.scss`.
 */
export const CLASSE_DIALOG_CALENDARIO = "dialog-calendario";

/**
 * 486834 / 493860 — este componente era um wrapper de `react-modern-calendar-datepicker`. Aquele
 * pacote está abandonado e é incompatível com o React 19: tem um `useEffect` SEM array de
 * dependências cujo cleanup lê `ref.current.removeEventListener` de forma live, e o React 19 zera
 * `ref.current` antes de rodar os cleanups passivos da subárvore que está sendo removida. Resultado:
 * TODO desmonte do calendário lançava `Cannot read properties of null (reading
 * 'removeEventListener')` — no menu "Ações" da barra de ferramentas, ao fechar o Dialog do
 * `InputData`/`InputDataCalendario` (ex.: `InputData_AbaEntrega_DataPrevisaoEntrega`), em qualquer
 * lugar. Sem limite de erro por perto, o React derrubava a tela inteira.
 *
 * A troca é para `react-calendar` (v4), que já era dependência deste projeto e já é usado em
 * `finalizar-orcamento/disponibilidade-capacidade-de-carga/`. Esse pacote não registra nenhum
 * listener em `ref.current` (zero ocorrências de `removeEventListener` no dist), então não tem como
 * repetir o defeito.
 *
 * A ASSINATURA (`CalendarioProps`) foi mantida idêntica de propósito — `BarraFerramentas`,
 * `InputData` e `InputDataCalendario` continuam passando `{ year, month, day }` em
 * `dataMinima`/`dataMaxima` e recebendo um `Date` em `onDataModificado`, sem alteração nenhuma.
 */

type CalendarioProps = {
  data: Date;
  dataMinima?: { year: number, month: number, day: number }
  dataMaxima?: { year: number, month: number, day: number }
  id?: string
  onDataModificado: (data: Date) => void;
}

// O limite de 2076 vinha do pacote antigo ("2077 da erro no calendario"). `react-calendar` não tem
// essa limitação, mas o default é mantido para não mudar o comportamento de quem já dependia dele.
const DATA_MAXIMA_PADRAO = new Date(2076, 11, 31)

const converterParaDate = (data?: { year: number, month: number, day: number }) =>
  data && typeof data.year === 'number' ? new Date(data.year, data.month - 1, data.day) : undefined

// Domingo em vermelho. O `react-calendar` só marca `--weekend`, que vale para sábado E domingo,
// então a distinção é feita aqui pela própria data (`getDay() === 0`) — independe da posição da
// coluna e do locale. A cor mora em Calendario.css (que é injetado dentro do shadow root).
// Fora da vista de mês (ano/década/século) o tile não é um dia, por isso o `view`.
const obterClasseDoTile = ({ date, view }: { date: Date, view: string }) =>
  view === "month" && date.getDay() === 0 ? "calendario__domingo" : null

const calendarStyles = (
  <>
    <style type="text/css">{css}</style>
    <style type="text/css">{estilosCalendario}</style>
  </>
);

const Calendario = React.memo((props: CalendarioProps) => {
  const isMounted = useRef(true)
  const calendarId = useMemo(() => props.id ? `Calendario_${props.id}` : `Calendario_${Date.now()}`, [props.id])

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const handleChange = useCallback(
    (data: Date) => {
      if (!isMounted.current) return
      props.onDataModificado(new Date(data.getFullYear(), data.getMonth(), data.getDate()))
    },
    [props.onDataModificado]
  )

  const dataMinima = useMemo(() => converterParaDate(props.dataMinima), [props.dataMinima])
  const dataMaxima = useMemo(() => converterParaDate(props.dataMaxima) ?? DATA_MAXIMA_PADRAO, [props.dataMaxima])

  return (
    <div id={calendarId} data-testid={calendarId}>
      {calendarStyles}
      <Calendar
        key={calendarId}
        value={props.data instanceof Date && !isNaN(props.data.getTime()) ? props.data : null}
        onClickDay={handleChange}
        minDate={dataMinima}
        maxDate={dataMaxima}
        locale="pt-BR"
        showNeighboringMonth={false}
        tileClassName={obterClasseDoTile}
        // Sem os saltos de década/ano: o calendário antigo também só navegava mês a mês.
        next2Label={null}
        prev2Label={null}
      />
    </div>
  )
})

export { Calendario, type CalendarioProps }
